import { css, cx } from "@emotion/css";
import { Tab, Tabs } from "@octopusdeploy/design-system-components";
import { useImperativeHandle, useCallback, useEffect } from "react";
import * as React from "react";
import { useTabConversion } from "./useTabConversion";
import type { RenderTabCallback } from "./useTabConversion";
export interface ControlledTabComponentProps {
    children: React.ReactNode;
    value: string;
    afterTabsElement?: JSX.Element;
    onChange: (value: string) => void;
    includeBorderBottom?: boolean;
    variant?: "scrollable" | "fullWidth";
}
export interface TabsRef {
    focusActiveTab: () => void;
}
export const ControlledTabsContainer = React.forwardRef<TabsRef, ControlledTabComponentProps>(({ onChange, variant, afterTabsElement, includeBorderBottom, children, value }, ref) => {
    const activeTabRef = React.useRef<HTMLDivElement>(null);
    useImperativeHandle(ref, () => ({
        focusActiveTab: () => {
            activeTabRef.current?.focus();
        },
    }));
    const defaultTabRender: RenderTabCallback = React.useMemo(() => ({ index, value: tabValue, label }) => <Tab ref={tabValue === value ? activeTabRef : undefined} tabIndex={index} value={tabValue} label={label} key={tabValue}/>, [value]);
    const { tabs, content, getTabDefinition } = useTabConversion(children, value, defaultTabRender);
    useEffect(() => {
        const tab = getTabDefinition(value);
        if (tab?.onActive)
            tab.onActive();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleTabChange = useCallback((event: React.ChangeEvent<{}>, tabValue: string) => {
        const tab = getTabDefinition(tabValue);
        if (tab && tab.onActive && tabValue !== value) {
            tab.onActive();
        }
        if (onChange) {
            onChange(tabValue);
        }
    }, [getTabDefinition, onChange, value]);
    return (<React.Fragment>
            <div className={cx(styles.tabsContainer, { [styles.border]: includeBorderBottom })}>
                <Tabs value={value} onChange={handleTabChange} variant={variant ?? "scrollable"}>
                    {tabs}
                </Tabs>
                {afterTabsElement}
            </div>
            {content}
        </React.Fragment>);
});
const styles = {
    tabsContainer: css({
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
    }),
    border: css({
        borderBottom: "2px solid var(--dividerLight)",
    }),
};
